import React, { useState, useEffect, useCallback } from "react";
import {
  Trophy, CheckCircle, GraduationCap, AlertTriangle, RotateCcw, Loader2, Calendar,
  FileDown, Printer, School, Download
} from "lucide-react";
import { gradesApi, apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== API RESPONSE HELPER ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.results)) return res.results;
  }
  return [];
};

const getErrorDetail = (err: any, fallback: string = ''): string => {
  const data = (err as any)?.response?.data as any;
  return data?.detail || data?.message || err?.message || fallback;
};

// CORRECT localStorage keys for your project
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// Recuperation school_id depuis auth-storage
const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) {
        return Number(state.user.school_id)
      }
    }
  } catch (e) {
    console.warn('Failed to get school_id from auth-storage:', e)
  }
  return null
}

// Recuperation academic_year_id depuis lybibal-selected-year-id
const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yearId) return Number(yearId)
  } catch (e) {
    console.warn('Failed to get year_id:', e)
  }
  return null
}

// Helper: charge avec school_id, fallback sans school_id si admin et résultat vide
const loadWithFallback = async (
  url: string,
  params: Record<string, any>,
  isPrivileged: boolean
): Promise<any[]> => {
  try {
    const res = await apiService.get(url, { params }).catch(() => ({ data: [] }));
    let data = extractData(res);
    if (data.length === 0 && params.school_id && isPrivileged) {
      const { school_id, ...paramsNoSchool } = params;
      const res2 = await apiService.get(url, { params: paramsNoSchool }).catch(() => ({ data: [] }));
      data = extractData(res2);
      if (data.length > 0) {
        console.warn(`[${url}] Fallback without school_id returned ${data.length} items`);
      }
    }
    return data;
  } catch (e) {
    console.warn(`[${url}] Load error:`, e);
    return [];
  }
};

interface HonorBoard {
  felicitations: Array<{ id: number; name: string; matricule: string; class_name: string; average: number }>;
  encouragements: Array<{ id: number; name: string; matricule: string; class_name: string; average: number }>;
  tableau_honneur: Array<{ id: number; name: string; matricule: string; class_name: string; average: number }>;
  avertissements: Array<{ id: number; name: string; matricule: string; class_name: string; average: number }>;
  blames: Array<{ id: number; name: string; matricule: string; class_name: string; average: number }>;
}

interface SchoolHeader {
  republic_fr: string;
  republic_en: string;
  motto_fr: string;
  motto_en: string;
  region: string;
  department: string;
  delegation: string;
  sub_division: string;
  school_name: string;
  school_name_en: string;
  address: string;
  po_box: string;
  logo_url: string;
}

export default function GradesHonorRollPage() {
  const { user, roleName, isAdmin, isSuperAdmin } = useAuthStore();
  const { lang } = useLang();

  // school_id et academic_year_id recuperes automatiquement
  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId)
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null)

  const t = {
    fr: {
      title: "Tableau d'Honneur",
      subtitle: 'Félicitations, encouragements et avertissements',
      congratulations: 'Félicitations',
      encouragement: 'Encouragements',
      honorBoard: "Tableau d'Honneur",
      warnings: 'Avertissements',
      blames: 'Blâmes',
      noData: 'Aucune donnée',
      selectClass: 'Sélectionnez une classe et une séquence',
      loading: 'Chargement...',
      refresh: 'Rafraîchir',
      error: 'Erreur',
      noYearSelected: 'Aucune année scolaire sélectionnée',
      yearRequired: 'Veuillez sélectionner une année scolaire dans le header',
      downloadList: 'Télécharger la liste',
      downloadCertificates: 'Télécharger les certificats',
      listDownloaded: 'Liste téléchargée',
      certificatesDownloaded: 'Certificats téléchargés',
      noStudentsToDownload: 'Aucun élève à télécharger',
      sortBy: 'Trier par',
      sortMerit: 'Mérite (moyenne)',
      sortAlpha: 'Alphabétique',
    },
    en: {
      title: 'Honor Board',
      subtitle: 'Congratulations, encouragements and warnings',
      congratulations: 'Congratulations',
      encouragement: 'Encouragements',
      honorBoard: 'Honor Board',
      warnings: 'Warnings',
      blames: 'Blames',
      noData: 'No data',
      selectClass: 'Select a class and sequence',
      loading: 'Loading...',
      refresh: 'Refresh',
      error: 'Error',
      noYearSelected: 'No academic year selected',
      yearRequired: 'Please select an academic year in the header',
      downloadList: 'Download list',
      downloadCertificates: 'Download certificates',
      listDownloaded: 'List downloaded',
      certificatesDownloaded: 'Certificates downloaded',
      noStudentsToDownload: 'No students to download',
      sortBy: 'Sort by',
      sortMerit: 'Merit (average)',
      sortAlpha: 'Alphabetical',
    }
  }[lang];

  const [honorBoard, setHonorBoard] = useState<HonorBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedSequence, setSelectedSequence] = useState<string>("");
  const [classes, setClasses] = useState<any[]>([]);
  const [sequences, setSequences] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<'merit' | 'alphabetical'>('merit');
  const [selectedClassSchoolId, setSelectedClassSchoolId] = useState<number | null>(null);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: 'RÉPUBLIQUE DU CAMEROUN',
    republic_en: 'REPUBLIC OF CAMEROON',
    motto_fr: 'Paix-Travail-Patrie',
    motto_en: 'Peace-Work-Fatherland',
    region: "RÉGION DE L'OUEST",
    department: 'DÉPARTEMENT DE LA MIFI',
    delegation: 'DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES',
    sub_division: 'ARRONDISSEMENT DE BAFOUSSAM II',
    school_name: 'LYCÉE BILINGUE DE BALENG',
    school_name_en: 'GBHS BALENG',
    address: 'BP : 1041 Baleng',
    po_box: 'P.O. Box : 1041 Baleng',
    logo_url: '',
  });

  const isAdminRole = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';

  // Refs pour éviter les re-subscriptions
  const schoolIdRef = React.useRef<number | null>(schoolId)
  const yearIdRef = React.useRef<number | null>(selectedAcademicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = selectedAcademicYearId }, [selectedAcademicYearId])

  // Construire les params de filtrage avec school_id et academic_year_id
  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {}
    if (schoolId) params.school_id = schoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [schoolId, selectedAcademicYearId])

  // Params pour les appels liés à une classe spécifique (utilise le school_id de la classe)
  const getClassApiParams = useCallback(() => {
    const params: Record<string, any> = {}
    const effectiveSchoolId = selectedClassSchoolId || schoolId
    if (effectiveSchoolId) params.school_id = effectiveSchoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [selectedClassSchoolId, schoolId, selectedAcademicYearId])

  // Listen for year-change events
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null

      // Guard: if nothing changed, do nothing to avoid loops
      if (newYearId && newYearId === yearIdRef.current && (newSchoolId === null || newSchoolId === schoolIdRef.current)) {
        return
      }

      if (newYearId && newYearId !== yearIdRef.current) {
        setSelectedAcademicYearId(newYearId)
        const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY)
        if (stored !== String(detail.year_id)) {
          try { localStorage.setItem(SELECTED_YEAR_ID_KEY, detail.year_id.toString()) } catch (err) { /* ignore */ }
        }
        if (detail.label) {
          const storedLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
          try { if (storedLabel !== detail.label) localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detail.label) } catch (err) { /* ignore */ }
          setCurrentAcademicYear({ id: newYearId, name: detail.label })
        }
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId)
        try { localStorage.setItem('lybibal-selected-school-id', newSchoolId.toString()); } catch (err) { /* ignore */ }
      }

      // Reset filters when year changes
      setSelectedClass("")
      setSelectedSequence("")
      setHonorBoard(null)

      toast.info(lang === 'fr' ? 'Année scolaire changée - rechargement des données' : 'Academic year changed - reloading data')
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== yearIdRef.current) {
          setSelectedAcademicYearId(newYearId)
          setSelectedClass("")
          setSelectedSequence("")
          setHonorBoard(null)
        }
      }
    }
    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [lang])

  // Sync year from storage au montage
  useEffect(() => {
    const yearId = getSelectedYearId()
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
    if (yearId) {
      setSelectedAcademicYearId(yearId)
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel })
    }

    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id')
      if (storedSchool) {
        const sid = Number(storedSchool)
        if (!isNaN(sid)) setSchoolId(sid)
      }
    } catch (err) {
      // ignore
    }

    // Charger l'en-tête de l'établissement depuis le localStorage (partagé avec bulletins)
    try {
      const savedHeader = localStorage.getItem('lybibal-school-header')
      if (savedHeader) setSchoolHeader(JSON.parse(savedHeader))
    } catch (e) { /* ignore */ }
  }, [])

  // Charger les classes et séquences avec filtres
  useEffect(() => {
    const loadFilters = async () => {
      if (!selectedAcademicYearId) {
        setClasses([])
        setSequences([])
        return
      }

      try {
        const filterParams = getFilterParams()
        const isPrivileged = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';

        const [classesData, seqsData] = await Promise.all([
          loadWithFallback('/classes', filterParams, isPrivileged),
          loadWithFallback('/grades/sequences', filterParams, isPrivileged),
        ]);

        // Filtrer les séquences par année scolaire (défensive)
        const filteredSeqs = Array.isArray(seqsData) && selectedAcademicYearId
          ? seqsData.filter((s: any) => {
              const matchesYear = (s.academic_year_id === undefined) ? true : Number(s.academic_year_id) === Number(selectedAcademicYearId);
              const matchesSchool = (s.school_id === undefined || !schoolId) ? true : Number(s.school_id) === Number(schoolId);
              return matchesYear && matchesSchool;
            })
          : (Array.isArray(seqsData) ? seqsData : [])

        setClasses(Array.isArray(classesData) ? classesData : []);
        setSequences(filteredSeqs);
      } catch (e) { 
        console.error('[GradesHonorRollPage] loadFilters error:', e); 
        setClasses([])
        setSequences([])
      }
    };
    loadFilters();
  }, [schoolId, selectedAcademicYearId, getFilterParams, isAdmin, isSuperAdmin, roleName]);

  const loadHonorBoard = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId) return;
    
    setLoading(true);
    try {
      const filterParams = getFilterParams()
      const params: any = {
        ...filterParams,
      };
      if (selectedClass) params.class_id = parseInt(selectedClass);
      if (selectedSequence) params.sequence_id = parseInt(selectedSequence);

      const res = await apiService.get('/grades/honor-board', { params }).catch(() => ({ data: null }));
      const data = (res as any)?.data ?? res
      setHonorBoard(data || null);
    } catch (err) {
      console.error(err);
      toast.error(t.error);
      setHonorBoard(null);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSequence, schoolId, selectedAcademicYearId, getFilterParams, t.error]);

  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      loadHonorBoard();
    }
  }, [loadHonorBoard]);

  // ==================== GÉNÉRATION HTML TABLEAU D'HONNEUR ====================
  const generateHonorRollHTML = (title: string, items: any[], watermarkText: string): string => {
    const h = schoolHeader;
    const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const todayEn = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    const rows = items.map((s, i) => `
      <tr style="background:${i % 2 === 0 ? 'white' : '#f9fafb'};font-size:10px;">
        <td style="border:1px solid black;padding:6px 8px;text-align:center;font-weight:bold;">${i + 1}</td>
        <td style="border:1px solid black;padding:6px 8px;font-weight:600;">${s.name || ''}</td>
        <td style="border:1px solid black;padding:6px 8px;text-align:center;">${s.matricule || ''}</td>
        <td style="border:1px solid black;padding:6px 8px;text-align:center;">${s.class_name || ''}</td>
        <td style="border:1px solid black;padding:6px 8px;text-align:center;font-weight:bold;color:#1e3a8a;">${s.average?.toFixed(2) || '0.00'}</td>
      </tr>
    `).join('');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title} - ${h.school_name}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    body { font-family: Arial, sans-serif; font-size: 10px; margin: 0; padding: 0; }
    .watermark {
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      z-index: -1; opacity: 0.06; pointer-events: none;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      gap: 60px; transform: rotate(-30deg);
    }
    .watermark span {
      font-size: 48px; font-weight: bold; color: #1e3a8a;
      text-transform: uppercase; letter-spacing: 4px; white-space: nowrap;
    }
    table { border-collapse: collapse; width: 100%; margin-top: 12px; }
    th, td { border: 1px solid black; padding: 6px 8px; }
    th { background: #f3f4f6; font-weight: bold; text-align: center; font-size: 10px; }
    .header { text-align: center; border-bottom: 2px solid black; padding-bottom: 8px; margin-bottom: 12px; }
    .header-grid { display: flex; justify-content: space-between; align-items: flex-start; }
    .header-col { flex: 1; }
    .header-col p { margin: 1px 0; font-size: 8px; }
    .header-col .bold { font-weight: bold; font-size: 8px; }
    .logo { width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; margin: 0 8px; }
    .school-name { text-align: center; margin: 4px 0; }
    .school-name h2 { margin: 0; font-size: 14px; color: #1e3a8a; font-weight: bold; }
    .title { text-align: center; margin-top: 16px; }
    .title h1 { margin: 0; font-size: 18px; font-weight: bold; text-transform: uppercase; color: #1e3a8a; }
    .title p { margin: 4px 0; font-size: 11px; font-weight: 600; }
    .footer { margin-top: 24px; text-align: center; font-size: 9px; color: #666; }
  </style>
</head>
<body>
    <!-- watermark removed from list -->
  <div class="header">
    <div class="header-grid">
      <div class="header-col" style="text-align:left;">
        <p class="bold">${h.republic_fr}</p>
        <p>${h.motto_fr}</p>
        <p style="margin-top:3px">${h.region}</p>
        <p>${h.department}</p>
        <p>${h.delegation}</p>
        <p>${h.sub_division}</p>
      </div>
      <div class="logo">
        ${h.logo_url ? `<img src="${h.logo_url}" style="width:45px;height:45px;object-fit:contain;" />` : '<div style="width:45px;height:45px;background:#1e3a8a;border-radius:50%;"></div>'}
      </div>
      <div class="header-col" style="text-align:right;">
        <p class="bold">${h.republic_en}</p>
        <p>${h.motto_en}</p>
        <p style="margin-top:3px">${h.region?.replace("RÉGION", "WEST REGION")}</p>
        <p>${h.department?.replace("DÉPARTEMENT", "DIVISION")}</p>
        <p>${h.delegation?.replace("DÉLÉGATION", "DELEGATION")}</p>
        <p>${h.sub_division?.replace("ARRONDISSEMENT", "SUB DIVISION")}</p>
      </div>
    </div>
    <div class="school-name">
      <h2>${h.school_name}</h2>
      <p style="font-size:8px;color:#666;">${h.school_name_en}</p>
      <p style="font-size:7px;color:#999;">${h.address} | ${h.po_box}</p>
    </div>
  </div>
  <div class="title">
    <h1>TABLEAU D'HONNEUR</h1>
    <p>HONOUR'S ROLL</p>
    <p style="font-size:12px;margin-top:8px;color:#333;">${title}</p>
    <p style="font-size:10px;color:#666;">Année scolaire ${currentAcademicYear?.name || ''} — ${today}</p>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:8%;">N°</th>
        <th style="width:35%;">NOMS ET PRÉNOMS</th>
        <th style="width:17%;">MATRICULE</th>
        <th style="width:20%;">CLASSE</th>
        <th style="width:20%;">MOYENNE / 20</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>
  <div class="footer">
    <p>Fait à ${h.sub_division?.replace("ARRONDISSEMENT DE ", "") || "Baleng"}, le ${today}</p>
    <p>Done in ${h.sub_division?.replace("ARRONDISSEMENT DE ", "") || "Baleng"}, on ${todayEn}</p>
    <p style="margin-top:12px;font-weight:bold;">Le Chef d'Établissement / The Principal</p>
    <div style="border-top:1px solid #333;width:150px;margin:20px auto 0;"></div>
  </div>
</body>
</html>`;
  };

  const generateCertificateHTML = (student: any, category: string, watermarkText: string): string => {
    const h = schoolHeader;
    const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const todayEn = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const yearName = currentAcademicYear?.name || '';
    const className = student.class_name || '';
    const studentName = student.name || '';
    const avg = student.average?.toFixed(2) || '0.00';
    const avgNum = student.average || 0;
    const lieu = h.sub_division?.replace("ARRONDISSEMENT DE ", "") || "Baleng";

    // Logique des cases basée sur la moyenne
    const checkEncouragements = avgNum >= 10;  // toujours vrai pour tableau d'honneur
    const checkFelicitations = avgNum >= 14;   // coché seulement si moyenne >= 14

    // Filigrane oblique répété (18 lignes)
    let wmRows = '';
    for (let i = 0; i < 18; i++) {
      wmRows += `<div style="font-size:34px;font-weight:bold;color:#1e3a8a;text-transform:uppercase;letter-spacing:3px;white-space:nowrap;padding:4px 0;text-align:center;width:100%;">${watermarkText}</div>`;
    }

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Certificat - ${studentName}</title>
  <style>
    @page { size: A5 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; margin: 0; padding: 0; font-size: 10px; background: #fff; }
    .page {
      position: relative;
      width: 100%; min-height: 100vh; display: flex; flex-direction: column;
      padding: 20px 24px; border: 4px double #1e3a8a; border-radius: 12px;
      margin: 4px; background: #fff; overflow: hidden;
    }
    .watermark-layer {
      position: absolute; top: -50%; left: -50%; width: 200%; height: 200%;
      z-index: 0; pointer-events: none; overflow: hidden;
      display: flex; flex-direction: column; justify-content: space-around; align-items: center;
      opacity: 0.06; transform: rotate(-30deg); transform-origin: center center;
    }
    .content-layer {
      position: relative; z-index: 1;
    }
    .main-title { text-align: center; margin: 16px 0 10px; }
    .main-title h1 { margin: 0; font-size: 20px; font-weight: bold; color: #800080; text-transform: uppercase; letter-spacing: 2px; }
    .subtitle { text-align: center; font-size: 9px; line-height: 1.6; margin-bottom: 24px; color: #333; }
    .subtitle b { font-weight: bold; color: #000; }
    .student-name-box {
      text-align: center; margin: 20px 0 24px;
      border: 2px solid #1e3a8a; border-radius: 8px; padding: 14px 10px;
      background: #f8fafc;
    }
    .student-name {
      font-size: 28px; font-weight: bold; color: #1e3a8a;
      text-transform: uppercase; letter-spacing: 1px; margin: 0;
    }
    .form-row { display: flex; align-items: center; margin-bottom: 10px; }
    .form-label { font-size: 9px; line-height: 1.4; min-width: 100px; }
    .form-label .fr { font-weight: bold; font-size: 10px; }
    .form-label .en { font-size: 8px; color: #555; font-style: italic; }
    .form-field { flex: 1; border-bottom: 1px solid #333; height: 20px; margin-left: 6px; font-size: 11px; padding-top: 2px; }
    .form-row-inline { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .inline-group { display: flex; align-items: center; flex: 1; }
    .inline-group .form-label { min-width: auto; white-space: nowrap; }
    .moyenne-row { display: flex; align-items: center; margin-bottom: 6px; }
    .moyenne-label { font-size: 9px; line-height: 1.4; margin-right: 8px; }
    .moyenne-label .fr { font-weight: bold; font-size: 10px; }
    .moyenne-label .en { font-size: 8px; color: #555; font-style: italic; }
    .moyenne-box { border: 2px solid #1e3a8a; background: #e8ecf4; padding: 4px 12px; font-weight: bold; font-size: 14px; display: flex; align-items: center; border-radius: 4px; }
    .moyenne-box span { color: #1e3a8a; font-size: 16px; }
    .checkbox-row { display: flex; align-items: center; margin-bottom: 6px; }
    .checkbox-box { width: 20px; height: 20px; border: 2px solid #1e3a8a; margin-right: 6px; display: flex; align-items: center; justify-content: center; background: #fff; flex-shrink: 0; }
    .checkbox-box.checked { background: #1e3a8a; }
    .checkbox-box.checked::after { content: '✓'; font-size: 12px; font-weight: bold; color: #fff; }
    .checkbox-label { font-size: 9px; }
    .checkbox-label .fr { font-weight: bold; color: #1e3a8a; font-size: 10px; }
    .checkbox-label .en { font-size: 8px; color: #555; font-style: italic; }
    .date-block { font-size: 9px; line-height: 1.5; color: #333; }
    .date-block .line { border-bottom: 1px solid #333; width: 130px; display: inline-block; height: 14px; margin: 0 2px; }
    .sign-block { margin-top: 16px; text-align: center; font-size: 9px; }
    .sign-line { border-top: 1px solid #333; width: 160px; margin: 0 auto 3px; padding-top: 3px; }
    .bottom-grid { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 20px; }
    .bottom-left { flex: 1; }
    .bottom-right { flex: 1; text-align: right; }
  </style>
</head>
<body>
  <div class="page">
    <div class="watermark-layer">
      ${wmRows}
    </div>
    <div class="content-layer">
      <div class="main-title">
        <h1>TABLEAU D'HONNEUR / HONOUR'S ROLL</h1>
      </div>

      <div class="subtitle">
        Le Conseil de Classe, en vertu des pouvoirs qui lui sont conférés décerne le <b>TABLEAU D'HONNEUR</b><br/>
        By virtue of the power conferred on it, the Class Council awards an <b>HONOUR'S ROLL</b>
      </div>

      <div class="form-row">
        <div class="form-label">
          <div class="fr">A l'élève</div>
          <div class="en">To the student</div>
        </div>
      </div>

      <div class="student-name-box">
        <div class="student-name">${studentName}</div>
      </div>

      <div class="form-row-inline">
        <div class="inline-group">
          <div class="form-label">
            <div class="fr">De la classe de</div>
            <div class="en">Of Class</div>
          </div>
          <div class="form-field">&nbsp;${className}</div>
        </div>
        <div class="inline-group">
          <div class="form-label">
            <div class="fr">de l'année</div>
            <div class="en">Of academic year</div>
          </div>
          <div class="form-field">&nbsp;${yearName}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-label">
          <div class="fr">Pour son travail durant la période</div>
          <div class="en">For his(her) work during the period</div>
        </div>
        <div class="form-field">&nbsp;</div>
      </div>

      <div class="bottom-grid">
        <div class="bottom-left">
          <div class="moyenne-row">
            <div class="moyenne-label">
              <div class="fr">Moyenne Obtenue</div>
              <div class="en">Average Obtained</div>
            </div>
            <div class="moyenne-box"><span>${avg}</span>&nbsp;/ 20</div>
          </div>
          <div style="margin-top:12px;">
            <div class="checkbox-row">
              <div class="checkbox-box ${checkEncouragements ? 'checked' : ''}"></div>
              <div class="checkbox-label">
                <div class="fr">AVEC ENCOURAGEMENTS</div>
                <div class="en">WITH CREDITS</div>
              </div>
            </div>
            <div class="checkbox-row">
              <div class="checkbox-box ${checkFelicitations ? 'checked' : ''}"></div>
              <div class="checkbox-label">
                <div class="fr">AVEC FÉLICITATIONS</div>
                <div class="en">WITH DISTINCTIONS</div>
              </div>
            </div>
          </div>
        </div>
        <div class="bottom-right">
          <div class="date-block">
            <div>Fait à <span class="line">&nbsp;${lieu}</span>, le <span class="line">&nbsp;${today}</span></div>
            <div style="font-size:8px;color:#666;margin-top:2px;">Done in <span class="line" style="width:100px;">&nbsp;${lieu}</span>, on <span class="line" style="width:100px;">&nbsp;${todayEn}</span></div>
          </div>
          <div class="sign-block">
            <div class="sign-line"><strong>Le Chef d'Établissement / The Principal</strong></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  const downloadHTML = (html: string, filename: string) => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadList = (title: string, items: any[]) => {
    if (!items || items.length === 0) {
      toast.info(lang === 'fr' ? 'Aucun élève à télécharger' : 'No students to download');
      return;
    }
    const className = items[0]?.class_name || selectedClass || 'all';
    const safeClass = String(className).replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const html = generateHonorRollHTML(title, items, schoolHeader.school_name);
    const safeTitle = title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    downloadHTML(html, `tableau_honneur_${safeTitle}_${safeClass}.html`);
    toast.success(lang === 'fr' ? 'Liste téléchargée' : 'List downloaded');
  };

  const handleDownloadCertificates = (title: string, items: any[]) => {
    if (!items || items.length === 0) {
      toast.info(lang === 'fr' ? 'Aucun élève à télécharger' : 'No students to download');
      return;
    }
    const className = items[0]?.class_name || selectedClass || 'all';
    const safeClass = String(className).replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const allHtml = items.map((s, i) => {
      const cert = generateCertificateHTML(s, title, schoolHeader.school_name);
      const sep = i < items.length - 1 ? '<div style="page-break-after:always;"></div>' : '';
      return cert + sep;
    }).join('\n');
    const fullDoc = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Certificats ${title}</title></head><body>${allHtml}</body></html>`;
    const safeTitle = title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    downloadHTML(fullDoc, `certificats_${safeTitle}_${safeClass}.html`);
    toast.success(lang === 'fr' ? 'Certificats téléchargés' : 'Certificates downloaded');
  };

  const renderSection = (
    title: string,
    icon: React.ReactNode,
    colorClass: string,
    bgClass: string,
    borderClass: string,
    items: any[]
  ) => {
    if (!items || items.length === 0) return null;
    return (
      <div className={`bg-white rounded-xl shadow-sm border ${borderClass} p-6`}>
        <h3 className={`text-lg font-semibold ${colorClass} flex items-center gap-2 mb-4`}>
          {icon}
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => (
            <div key={s.id} className={`${bgClass} rounded-lg p-4 border ${borderClass} hover:shadow-md transition-shadow`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${bgClass} flex items-center justify-center ${colorClass} font-bold`}>
                  {s.name?.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{s.name}</p>
                  <p className="text-sm text-gray-500">{s.matricule || '-'} — {s.class_name || '-'}</p>
                </div>
                <span className={`text-lg font-bold ${colorClass}`}>{s.average?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Si pas d'année scolaire sélectionnée, afficher un message
  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Trophy className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
            <span className="text-xs text-gray-400">
              school_id: {schoolId || "-"}
            </span>
          </div>
        )}
      </div>

      {/* Filters + Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-3">
        <select
          value={selectedClass}
          onChange={(e) => {
            const cid = e.target.value
            setSelectedClass(cid)
            setHonorBoard(null)
            const cls = classes.find((c: any) => String(c.id) === cid)
            setSelectedClassSchoolId(cls?.school_id ? Number(cls.school_id) : null)
          }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] min-w-[180px]"
        >
          <option value="">{lang === 'fr' ? 'Toutes les classes' : 'All classes'}</option>
          {classes.map((c: any) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={selectedSequence}
          onChange={(e) => {
            setSelectedSequence(e.target.value)
            setHonorBoard(null)
          }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] min-w-[180px]"
        >
          <option value="">{lang === 'fr' ? 'Toutes les séquences' : 'All sequences'}</option>
          {sequences.map((s: any) => (
            <option key={s.id} value={s.id}>{s.label || s.name}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'merit' | 'alphabetical')}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
        >
          <option value="merit">{t.sortMerit}</option>
          <option value="alphabetical">{t.sortAlpha}</option>
        </select>
        <div className="h-6 w-px bg-gray-200" />
        <button
          onClick={loadHonorBoard}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
          title={t.refresh}
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <button
          onClick={() => {
            if (!honorBoard) return;
            const allItems = [
              ...(honorBoard.felicitations || []),
              ...(honorBoard.encouragements || []),
              ...(honorBoard.tableau_honneur || []),
              ...(honorBoard.avertissements || []),
              ...(honorBoard.blames || []),
            ];
            if (allItems.length === 0) {
              toast.info(t.noStudentsToDownload);
              return;
            }
            const sorted = sortBy === 'alphabetical'
              ? [...allItems].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              : [...allItems].sort((a, b) => (b.average || 0) - (a.average || 0));
            handleDownloadList(lang === 'fr' ? 'Tous les élèves' : 'All students', sorted);
          }}
          disabled={!honorBoard}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
        >
          <FileDown className="w-4 h-4" />
          {t.downloadList}
        </button>
        <button
          onClick={() => {
            if (!honorBoard) return;
            const allItems = [
              ...(honorBoard.felicitations || []),
              ...(honorBoard.encouragements || []),
              ...(honorBoard.tableau_honneur || []),
            ];
            if (allItems.length === 0) {
              toast.info(t.noStudentsToDownload);
              return;
            }
            const sorted = sortBy === 'alphabetical'
              ? [...allItems].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              : [...allItems].sort((a, b) => (b.average || 0) - (a.average || 0));
            handleDownloadCertificates(lang === 'fr' ? 'Tous les élèves' : 'All students', sorted);
          }}
          disabled={!honorBoard}
          className="px-3 py-2 bg-[#1e3a8a] hover:bg-[#152a5e] text-white rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
        >
          <Printer className="w-4 h-4" />
          {t.downloadCertificates}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t.loading}</span>
        </div>
      ) : !honorBoard ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{t.selectClass}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {renderSection(
            `${t.congratulations} (≥ 16)`,
            <Trophy className="w-5 h-5" />,
            'text-emerald-700',
            'bg-emerald-50',
            'border-emerald-200',
            honorBoard.felicitations || []
          )}
          {renderSection(
            `${t.encouragement} (14-16)`,
            <CheckCircle className="w-5 h-5" />,
            'text-blue-700',
            'bg-blue-50',
            'border-blue-200',
            honorBoard.encouragements || []
          )}
          {renderSection(
            `${t.honorBoard} (12-14)`,
            <GraduationCap className="w-5 h-5" />,
            'text-cyan-700',
            'bg-cyan-50',
            'border-cyan-200',
            honorBoard.tableau_honneur || []
          )}
          {renderSection(
            `${t.warnings} (8-10)`,
            <AlertTriangle className="w-5 h-5" />,
            'text-orange-700',
            'bg-orange-50',
            'border-orange-200',
            honorBoard.avertissements || []
          )}
          {renderSection(
            `${t.blames} (< 8)`,
            <AlertTriangle className="w-5 h-5" />,
            'text-red-700',
            'bg-red-50',
            'border-red-200',
            honorBoard.blames || []
          )}
          {(!honorBoard.felicitations?.length && 
            !honorBoard.encouragements?.length && 
            !honorBoard.tableau_honneur?.length && 
            !honorBoard.avertissements?.length && 
            !honorBoard.blames?.length) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">{t.noData}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}